import * as React from 'react';

import { enumUnit, type Unit, Weight } from '@rematter/frontend-lib-api-client';
import {
  cn,
  Col,
  convertWeight,
  humanReadableWeight,
  Money,
  Row,
  TouchNumberField,
  Widget,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideEqual } from 'lucide-react';

import {
  $footerFocusedField,
  $itemsInterfaceDisabled,
  $itemsInterfaceEntries,
  $itemsInterfaceInventoryLocked,
  $itemsInterfaceTicketStatus,
  $updateHeavyWeight,
  $updateLightWeight,
} from '../_state';
import { WeightField } from '../_types';
import { useTicketSummary } from '../hooks/useTicketSummary';

import { useTakeWeightListener } from '~/modules/trade/hooks/useScaleReading';
import { useScaleTakeWeight } from '~/modules/trade/hooks/useScaleWeight';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { toWeight } from '~/modules/trade/utils/weight';
import { useUIPreferences } from '~/shared/hooks';

const WEIGHT_FORMAT_OPTIONS = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

type TicketSummaryFooterProps = {
  className?: string;
};

type FooterSectionProps = {
  label: string;
  value: React.ReactNode;
  variant?: 'default' | 'money' | 'negative';
  className?: string;
};

//
// ⚛️ ⚛️ ⚛️ DIVIDER ⚛️ ⚛️ ⚛️
//
const Divider = () => <div className="@Divider h-8 w-px bg-neutral/20" />;

//
// ⚛️ ⚛️ ⚛️ FOOTER SECTION ⚛️ ⚛️ ⚛️
//
const FooterSection: React.FC<FooterSectionProps> = ({
  label,
  value,
  variant = 'default',
  className,
}) => (
  <Col xEnd className={cn('@FooterSection gap-0.5', className)}>
    <div className="text-[0.563rem] font-medium text-neutral/60 uppercase">
      {label}
    </div>
    <div
      className={cn(
        'flex h-11 items-center text-lg font-medium',
        variant === 'negative' && 'text-failure',
        variant === 'money' && 'text-success',
      )}
    >
      {value}
    </div>
  </Col>
);

//
// ⚛️ ⚛️ ⚛️ ITEMS SUMMARY FOOTER ⚛️ ⚛️ ⚛️
//
export const ItemsSummaryFooter: React.FC<TicketSummaryFooterProps> = ({
  className,
}) => {
  const t = useTranslations();

  // Atoms
  const ticketStatus = useAtomValue($itemsInterfaceTicketStatus);
  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const { heavyWeight: localHeavyWeight, lightWeight: localLightWeight } =
    useAtomValue($itemsInterfaceEntries);
  const [focusedField, setFocusedField] = useAtom($footerFocusedField);
  const setHeavyWeight = useSetAtom($updateHeavyWeight);
  const setLightWeight = useSetAtom($updateLightWeight);

  const { touchModeEnabled } = useUIPreferences();
  const { takeWeight } = useScaleTakeWeight();
  const totals = useTicketSummary();

  // Callbacks
  const handleWeightChange = React.useCallback(
    (field: WeightField, scaleValue: number, scaleUnit: Unit) => {
      const valueInPounds = convertWeight(
        scaleValue,
        scaleUnit,
        enumUnit.Pounds,
      );
      const weight: Weight | null =
        valueInPounds > 0 ? toWeight(valueInPounds, enumUnit.Pounds) : null;

      if (field === WeightField.Heavy) setHeavyWeight(weight);
      else setLightWeight(weight);
    },
    [setHeavyWeight, setLightWeight],
  );

  useTakeWeightListener(
    ({ weight, unitOfMeasurement }) => {
      if (focusedField === WeightField.Heavy) {
        handleWeightChange(WeightField.Heavy, weight, unitOfMeasurement);
      } else if (focusedField === WeightField.Light) {
        handleWeightChange(WeightField.Light, weight, unitOfMeasurement);
      }
    },
    [focusedField, handleWeightChange],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ' ' && focusedField) {
        e.preventDefault();
        const reading = takeWeight();

        if (reading?.stable) {
          handleWeightChange(
            focusedField,
            reading.grossWeight,
            reading.unitOfMeasurement ?? enumUnit.Pounds,
          );
        }
      }
    },
    [focusedField, takeWeight, handleWeightChange],
  );

  const handleHeavyFocus = React.useCallback(
    () => setFocusedField(WeightField.Heavy),
    [setFocusedField],
  );
  const handleLightFocus = React.useCallback(
    () => setFocusedField(WeightField.Light),
    [setFocusedField],
  );
  const isEditable =
    !disabled &&
    !inventoryLocked &&
    ticketStatus !== 'Closed' &&
    ticketStatus !== 'Voided';
  const hasHeavyWeight = (localHeavyWeight?.value ?? 0) > 0;

  const heavyDisplayValue = localHeavyWeight?.value ?? 0;
  const lightDisplayValue = localLightWeight?.value ?? 0;

  return (
    <Widget
      className={cn(
        '@ItemsSummaryFooter',
        'flex flex-wrap items-center justify-between gap-x-2 gap-y-1 p-1.5 px-3',
        className,
      )}
    >
      <FooterSection
        label={t.scaleTicketPro.footer.heavyWeight}
        value={
          <TouchNumberField
            aria-label={t.scaleTicketPro.footer.heavyWeight}
            value={heavyDisplayValue}
            onChange={(v) =>
              handleWeightChange(WeightField.Heavy, v, enumUnit.Pounds)
            }
            formatOptions={WEIGHT_FORMAT_OPTIONS}
            touchModeEnabled={touchModeEnabled}
            suffix={t.scaleTicketPro.footer.weightUnit}
            isDisabled={!isEditable}
            onFocus={handleHeavyFocus}
            onKeyDown={handleKeyDown}
            className="h-11 w-20"
          />
        }
      />

      <Row className="mt-4 h-11 px-2">
        <LucideEqual className="h-4 w-4 text-neutral/40" />
      </Row>

      <FooterSection
        label={t.scaleTicketPro.footer.lightWeight}
        value={
          <TouchNumberField
            aria-label={t.scaleTicketPro.footer.lightWeight}
            value={lightDisplayValue}
            onChange={(v) =>
              handleWeightChange(WeightField.Light, v, enumUnit.Pounds)
            }
            formatOptions={WEIGHT_FORMAT_OPTIONS}
            touchModeEnabled={touchModeEnabled}
            suffix={t.scaleTicketPro.footer.weightUnit}
            isDisabled={!isEditable}
            onFocus={handleLightFocus}
            onKeyDown={handleKeyDown}
            variant={isEditable ? 'subtle' : undefined}
            className="h-11 w-20"
          />
        }
      />

      <FooterSection
        label={t.scaleTicketPro.footer.materials}
        value={humanReadableWeight(totals.materials)}
      />

      <FooterSection
        label={t.scaleTicketPro.footer.deductions}
        value={humanReadableWeight(totals.deductionsWeight)}
        variant="negative"
      />

      <FooterSection
        label={t.scaleTicketPro.footer.tareOnTruck}
        value={humanReadableWeight(totals.tareOnTruck)}
      />

      <FooterSection
        label={
          totals.outstanding >= 0
            ? t.scaleTicketPro.footer.outstanding
            : t.scaleTicketPro.footer.surplus
        }
        value={humanReadableWeight(totals.outstanding)}
        className={cn(
          hasHeavyWeight && totals.outstanding !== 0 && 'text-warning',
          hasHeavyWeight && totals.outstanding === 0 && 'text-success',
          !hasHeavyWeight && 'opacity-30',
        )}
      />

      <Divider />

      <FooterSection
        label={t.scaleTicketPro.footer.deductionsAmount}
        value={
          <Money
            amount={totals.deductionsTotal}
            currencyCode={totals.currencyCode}
          />
        }
        variant="negative"
      />

      <FooterSection
        label={t.scaleTicketPro.footer.freightCharges}
        value={
          <Money
            amount={totals.freightTotal}
            currencyCode={totals.currencyCode}
          />
        }
        variant="negative"
      />

      <Divider />

      <FooterSection
        label={t.scaleTicketPro.footer.purchaseTotal}
        value={
          <span className="text-xl font-bold text-success">
            <Money
              amount={totals.purchaseTotal}
              currencyCode={totals.currencyCode}
            />
          </span>
        }
        className="pl-2"
      />
    </Widget>
  );
};
